import type { CameraRig } from '../systems/CameraRig';

export type LookMode = 'chase' | 'firstPerson';

/**
 * Pointer-lock lifecycle, exposed so the host can react (HUD affordance) and the
 * test harness can assert. `unavailable` means look does not use pointer lock at
 * all: not first person, a touch profile, or a forced test state.
 */
export type LookLockState = 'locked' | 'unlocked' | 'unavailable';

export interface LookDelta {
  yaw: number;
  pitch: number;
}

export interface LookOptions {
  /** Look sensitivity in radians per pixel (three.js pointer-lock default). */
  sensitivity?: number;
  /** Pitch clamp in radians (default ±85°). */
  pitchLimit?: number;
  /** Lock predicate seam; defaults to the real `document.pointerLockElement`. */
  isLocked?: () => boolean;
  /** Notified on every lifecycle transition (HUD affordance, diagnostics). */
  onLockStateChange?: (state: LookLockState) => void;
}

const DEFAULT_SENSITIVITY = 0.002;
const DEFAULT_PITCH_LIMIT = (85 * Math.PI) / 180;

/** Delay between the bounded re-lock attempts issued after a focus loss (ms). */
const LOCK_RETRY_DELAY_MS = 120;
/** Hard cap on re-lock attempts per focus return, so a refusal cannot loop. */
const LOCK_RETRY_ATTEMPTS = 2;
/** Quiet period after any unlock, so a just-released lock is not re-requested. */
const LOCK_COOLDOWN_MS = 1000;
/** Window in which an observed focus loss explains a subsequent unlock. */
const FOCUS_LOSS_WINDOW_MS = 2000;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Convert pointer travel (CSS px) into a deck-relative look delta (radians).
 *
 * `fp.yaw` is composed as `Euler(pitch, yaw - PI/2, 0, 'YXZ')`, so yaw 0 looks
 * along the bow (+X) and a *larger* yaw turns toward port (-Z). Rightward input
 * therefore decreases yaw — the same convention as three.js
 * `PointerLockControls` (`_euler.y -= movementX`) and the chase-orbit drag
 * (`orbitYaw -= dx`). Downward input looks down, so pitch decreases too.
 *
 * Pure on purpose: the direction contract is unit-tested without a DOM.
 */
export function lookDeltaFromPointer(dx: number, dy: number, sensitivity: number): LookDelta {
  return { yaw: -dx * sensitivity, pitch: -dy * sensitivity };
}

/**
 * First-person look input: desktop pointer-lock `movementX/Y` plus a
 * right-half-of-canvas drag for touch. Deltas are accumulated 1:1 (no damping)
 * and folded into `CameraRig.fp` by `applyTo` each frame.
 *
 * Look is gated to first-person mode: in chase mode the orbit drag is owned by
 * `Game.installOrbitControls` and this controller stays silent.
 *
 * Pointer lock is a lifecycle, not a getter: the document drops the lock when the
 * tab or window loses focus (Pointer Lock 2.0 §8), so the controller listens for
 * `pointerlockchange`/`pointerlockerror`, re-acquires the lock when focus returns
 * (allowed without a gesture, because the lock was not exited by the default
 * unlock gesture), and never re-requests after a deliberate Esc — that needs a
 * user gesture, which the resume affordance provides. While unlocked, a held
 * mouse-button drag still rotates the view so the camera is never stuck.
 */
export class LookController {
  private mode: LookMode = 'chase';
  private readonly sensitivity: number;
  private readonly pitchLimit: number;
  private readonly isLockedFn: () => boolean;
  private readonly onLockStateChange: ((state: LookLockState) => void) | undefined;

  /** Accumulated, poll-and-clear look deltas in radians. */
  private pendingYaw = 0;
  private pendingPitch = 0;

  private readonly tmpLook: LookDelta = { yaw: 0, pitch: 0 };

  // Touch/pen (and unlocked-mouse) drag state.
  private dragging = false;
  private dragId: number | null = null;
  private lastX = 0;
  private lastY = 0;

  // ── Pointer-lock lifecycle ────────────────────────────────────────────
  private lockPhase: LookLockState = 'unavailable';
  /** Host switch: touch profiles never use pointer lock. */
  private lockAvailable = true;
  /** Test seam: forces the lock predicate without a real lock. */
  private lockedOverride: boolean | null = null;
  /** Set before our own `exitPointerLock()`, so it never triggers a self-heal. */
  private expectUnlock = false;
  /** `performance.now()` of the last observed blur / hidden transition. */
  private focusLostAt = 0;
  /** True while a focus-loss unlock is waiting for (or retrying) a re-acquire. */
  private pendingSelfHeal = false;
  private retryCount = 0;
  private retryTimer: number | null = null;
  private cooldownUntil = 0;
  private lockAttempts = 0;
  private disposed = false;

  private readonly onMouseMove = (event: MouseEvent) => {
    if (this.mode !== 'firstPerson' || !this.locked) return;
    this.accumulate(event.movementX, event.movementY, this.sensitivity);
  };

  private readonly onPointerDown = (event: PointerEvent) => {
    if (this.mode !== 'firstPerson') return;
    // Pointer lock owns the mouse; drag look is the locked-out fallback.
    if (event.pointerType === 'mouse' && this.locked) return;
    // Touch/pen: the left half belongs to the movement stick, look drags right.
    if (event.pointerType !== 'mouse') {
      const rect = this.canvas.getBoundingClientRect();
      if (event.clientX < rect.left + rect.width / 2) return;
    }
    if (this.dragging) return;
    this.dragging = true;
    this.dragId = event.pointerId;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    try {
      this.canvas.setPointerCapture(event.pointerId);
    } catch {
      // Synthetic test events do not always have a capturable pointer id.
    }
    // Never swallow the mouse gesture: the canvas click is what re-requests lock.
    if (event.pointerType !== 'mouse') event.preventDefault();
  };

  private readonly onPointerMove = (event: PointerEvent) => {
    if (!this.dragging || event.pointerId !== this.dragId) return;
    const dx = event.clientX - this.lastX;
    const dy = event.clientY - this.lastY;
    this.lastX = event.clientX;
    this.lastY = event.clientY;
    // Touch coordinates are CSS pixels; scale by devicePixelRatio so a physical
    // finger travel feels the same as an equivalent desktop mouse travel.
    const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    this.accumulate(dx, dy, this.sensitivity * dpr);
    event.preventDefault();
  };

  private readonly onPointerEnd = (event: PointerEvent) => {
    if (event.pointerId !== this.dragId) return;
    this.dragging = false;
    this.dragId = null;
  };

  private readonly onPointerLockChange = () => {
    if (this.locked) {
      this.pendingSelfHeal = false;
      this.cooldownUntil = 0;
      this.clearRetry();
      this.refreshLockState();
      return;
    }
    if (this.expectUnlock) {
      // Our own release (leaving first person): never self-heal.
      this.expectUnlock = false;
      this.refreshLockState();
      return;
    }
    this.cooldownUntil = this.now() + LOCK_COOLDOWN_MS;
    // The focus/visibility change that caused this may still be settling, so
    // classify the cause once the event loop drains.
    queueMicrotask(() => this.classifyLostLock());
  };

  private readonly onPointerLockError = () => {
    // The request failed (no gesture, cooldown, unsupported); the affordance is
    // the recovery path. Reported through the state, not an unhandled rejection.
    this.clearRetry();
    this.refreshLockState();
  };

  private readonly onWindowBlur = () => {
    if (this.mode !== 'firstPerson') return;
    this.focusLostAt = this.now();
  };

  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.onFocusReturn();
    else this.onWindowBlur();
  };

  private readonly onWindowFocus = () => {
    this.onFocusReturn();
  };

  constructor(
    private readonly canvas: HTMLCanvasElement,
    opts: LookOptions = {},
  ) {
    this.sensitivity = opts.sensitivity ?? DEFAULT_SENSITIVITY;
    this.pitchLimit = opts.pitchLimit ?? DEFAULT_PITCH_LIMIT;
    this.isLockedFn = opts.isLocked ?? (() => document.pointerLockElement === this.canvas);
    this.onLockStateChange = opts.onLockStateChange;

    // Keep browser scroll/pinch gestures from eating the look drag.
    this.canvas.style.touchAction = 'none';

    document.addEventListener('mousemove', this.onMouseMove);
    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerEnd);
    this.canvas.addEventListener('pointercancel', this.onPointerEnd);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    document.addEventListener('pointerlockerror', this.onPointerLockError);
    window.addEventListener('focus', this.onWindowFocus);
    window.addEventListener('blur', this.onWindowBlur);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** Poll-and-clear the accumulated look delta since the last frame. */
  readLook(target: LookDelta): LookDelta {
    target.yaw = this.pendingYaw;
    target.pitch = this.pendingPitch;
    this.pendingYaw = 0;
    this.pendingPitch = 0;
    return target;
  }

  /** Gate look capture: orbit drags stay in chase; look is first-person only. */
  setMode(mode: LookMode): void {
    this.mode = mode;
    if (mode !== 'firstPerson') {
      this.dragging = false;
      this.dragId = null;
      this.pendingSelfHeal = false;
      this.clearRetry();
    }
    this.refreshLockState();
  }

  /** Current pointer-lock lifecycle state (HUD affordance, diagnostics). */
  get lockState(): LookLockState {
    return this.lockPhase;
  }

  /** Pointer-lock requests issued (diagnostics; intent, not API calls). */
  get lockRequestCount(): number {
    return this.lockAttempts;
  }

  get locked(): boolean {
    return this.lockedOverride ?? this.isReallyLocked();
  }

  /** Host switch for touch profiles, where pointer lock is not used at all. */
  setLockAvailable(available: boolean): void {
    if (this.lockAvailable === available) return;
    this.lockAvailable = available;
    if (!available) {
      this.pendingSelfHeal = false;
      this.clearRetry();
    }
    this.refreshLockState();
  }

  /** Test/replay seam: force the lock predicate (`null` restores the real one). */
  setLockedOverride(locked: boolean | null): void {
    if (this.lockedOverride === locked) return;
    this.lockedOverride = locked;
    if (locked === true) {
      this.pendingSelfHeal = false;
      this.clearRetry();
    } else if (locked === false) {
      // The seam cannot tell Esc from a focus loss, so keep a forced loss
      // eligible for the focus-return self-heal (the case tests exercise).
      this.pendingSelfHeal = true;
    }
    this.refreshLockState();
  }

  /** Request desktop pointer lock (raw unadjusted movement where supported). */
  requestLock(): void {
    if (this.disposed || !this.lockAvailable || this.mode !== 'firstPerson') return;
    if (this.locked) return;
    this.lockAttempts += 1;
    this.requestPointerLock(true);
  }

  exitLock(): void {
    this.pendingSelfHeal = false;
    this.clearRetry();
    if (this.isReallyLocked()) {
      // Ours, so the upcoming `pointerlockchange` must not start a self-heal.
      this.expectUnlock = true;
      document.exitPointerLock();
    }
    this.refreshLockState();
  }

  /** Fold the accumulated look delta into the rig's FP yaw/pitch (deck-relative). */
  applyTo(cameraRig: CameraRig): void {
    const rig = cameraRig as CameraRig & { fp: { yaw: number; pitch: number } };
    const delta = this.readLook(this.tmpLook);
    rig.fp.yaw += delta.yaw;
    rig.fp.pitch = clamp(rig.fp.pitch + delta.pitch, -this.pitchLimit, this.pitchLimit);
  }

  dispose(): void {
    this.disposed = true;
    this.clearRetry();
    document.removeEventListener('mousemove', this.onMouseMove);
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerEnd);
    this.canvas.removeEventListener('pointercancel', this.onPointerEnd);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    document.removeEventListener('pointerlockerror', this.onPointerLockError);
    window.removeEventListener('focus', this.onWindowFocus);
    window.removeEventListener('blur', this.onWindowBlur);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.canvas.style.touchAction = '';
  }

  private accumulate(dx: number, dy: number, sensitivity: number): void {
    const delta = lookDeltaFromPointer(dx, dy, sensitivity);
    this.pendingYaw += delta.yaw;
    this.pendingPitch += delta.pitch;
  }

  private isReallyLocked(): boolean {
    return this.isLockedFn();
  }

  private computeLockState(): LookLockState {
    if (!this.lockAvailable || this.mode !== 'firstPerson') return 'unavailable';
    return this.locked ? 'locked' : 'unlocked';
  }

  private refreshLockState(): void {
    const next = this.computeLockState();
    if (next === this.lockPhase) return;
    this.lockPhase = next;
    this.onLockStateChange?.(next);
  }

  /**
   * Decide why the lock went away. Only a focus/visibility loss is self-healing;
   * a deliberate Esc (document focused and visible, no recent blur) waits for the
   * affordance, because browsers refuse an immediate re-request after it.
   */
  private classifyLostLock(): void {
    if (this.disposed || this.locked) return;
    this.refreshLockState();
    if (this.mode !== 'firstPerson' || !this.lockAvailable) return;
    const focusLossExplains = this.now() - this.focusLostAt < FOCUS_LOSS_WINDOW_MS;
    if (focusLossExplains || document.visibilityState !== 'visible' || !document.hasFocus()) {
      this.pendingSelfHeal = true;
      return;
    }
    this.pendingSelfHeal = false;
    this.clearRetry();
  }

  private onFocusReturn(): void {
    if (this.disposed || this.mode !== 'firstPerson' || !this.lockAvailable) return;
    if (this.locked) return;
    this.refreshLockState();
    if (this.pendingSelfHeal) this.scheduleSelfHeal();
  }

  private scheduleSelfHeal(): void {
    this.retryCount = 0;
    this.queueRetry();
  }

  private queueRetry(): void {
    if (this.retryTimer !== null) return;
    if (this.retryCount >= LOCK_RETRY_ATTEMPTS) {
      this.pendingSelfHeal = false;
      return;
    }
    const wait = Math.max(LOCK_RETRY_DELAY_MS, this.cooldownUntil - this.now());
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      if (this.disposed || this.mode !== 'firstPerson' || this.locked || !this.lockAvailable) return;
      this.retryCount += 1;
      this.requestLock();
      this.queueRetry();
    }, wait);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.retryCount = 0;
  }

  /**
   * One pointer-lock request, tolerating both WebIDL shapes: Chromium/Safari
   * return a promise that rejects when the browser refuses (missing gesture, the
   * post-Esc cooldown, `unadjustedMovement` unsupported), while Firefox returns
   * `undefined` and reports through `pointerlockchange`/`pointerlockerror`.
   */
  private requestPointerLock(raw: boolean): void {
    let result: unknown;
    try {
      result = raw
        ? this.canvas.requestPointerLock({ unadjustedMovement: true })
        : this.canvas.requestPointerLock();
    } catch {
      // Synchronous throw (older engines, exotic canvases): retry without raw
      // input, otherwise fall back to the affordance.
      if (raw) this.requestPointerLock(false);
      else this.refreshLockState();
      return;
    }
    const maybePromise = result as Promise<void> | null | undefined;
    if (!maybePromise || typeof maybePromise.then !== 'function') return;
    void maybePromise.catch((error: unknown) => {
      if (raw && (error as DOMException | undefined)?.name === 'NotSupportedError') {
        this.requestPointerLock(false);
        return;
      }
      // Refused: the look state stays `unlocked` and the affordance is shown by
      // the host. Never let this surface as an unhandled rejection.
      this.refreshLockState();
    });
  }

  private now(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }
}
